import { isCycleEvaluationGroupReadOnly } from './cycleEvaluationGroupReadOnly';
import { RouteName } from '../types';

// Type-only to avoid a circular import through `queryClient`.
import type {
    CycleAudiencePayload,
    CycleCommunicationBlockPayload,
    CycleEvaluationGroupUpdatePayload,
} from '@/api/cycle-evaluation-services/cycle-evaluation-group/cycleEvaluationGroupMutations';
import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { FrequencyValue } from '@/constants/Frequency';
import type {
    CycleEvaluationGroup,
    CycleEvaluationGroupChild,
    CycleEvaluationGroupStatusValue,
} from '@/types/cycle-evaluation/CycleEvaluationGroup';

import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import {
    CycleEvaluationGroupSteps,
    type CycleEvaluationGroupStepsType,
} from '@/constants/cycle-evaluation/CycleEvaluationGroupSteps';

/** Canonical step order. The user-facing rank is this array's index after filtering. */
export const CYCLE_EVALUATION_GROUP_STEP_ORDER: readonly CycleEvaluationGroupStepsType[] = [
    CycleEvaluationGroupSteps.CYCLE_INFORMATION,
    CycleEvaluationGroupSteps.EVALUATION_ELEMENTS,
    CycleEvaluationGroupSteps.FEEDBACK_360_SETTINGS,
    CycleEvaluationGroupSteps.DEADLINES,
    CycleEvaluationGroupSteps.COMMUNICATIONS,
];

const STEP_PATHS: Readonly<Record<CycleEvaluationGroupStepsType, string>> = {
    [CycleEvaluationGroupSteps.CYCLE_INFORMATION]: 'cycle-information',
    [CycleEvaluationGroupSteps.EVALUATION_ELEMENTS]: 'evaluation-elements',
    [CycleEvaluationGroupSteps.FEEDBACK_360_SETTINGS]: 'feedback-360-settings',
    [CycleEvaluationGroupSteps.DEADLINES]: 'deadlines',
    [CycleEvaluationGroupSteps.COMMUNICATIONS]: 'communications',
};

const PATH_STEPS: Readonly<Record<string, CycleEvaluationGroupStepsType>> = Object.fromEntries(
    Object.entries(STEP_PATHS).map(([step, path]) => [path, step as CycleEvaluationGroupStepsType]),
);

export const isCycleEvaluationGroupStep = (
    value?: string,
): value is CycleEvaluationGroupStepsType =>
    CYCLE_EVALUATION_GROUP_STEP_ORDER.includes(value as CycleEvaluationGroupStepsType);

export const getCycleEvaluationGroupStepPath = (step: string): string | undefined =>
    STEP_PATHS[step as CycleEvaluationGroupStepsType];

export const getCycleEvaluationGroupStepFromPath = (
    path: string,
): CycleEvaluationGroupStepsType | undefined => PATH_STEPS[path];

export const NEW_CYCLE_EVALUATION_GROUP_ID = 'new';

export const isNewCycleEvaluationGroupRoute = (cycleEvaluationGroupId?: string): boolean =>
    cycleEvaluationGroupId === NEW_CYCLE_EVALUATION_GROUP_ID;

export const getNewCycleEvaluationGroupPath = (routePrefix: string): string =>
    `${routePrefix}/${STEP_PATHS[CycleEvaluationGroupSteps.CYCLE_INFORMATION]}/${NEW_CYCLE_EVALUATION_GROUP_ID}`;

export interface CycleEvaluationGroupStepVisibility {
    hasFeedback360: boolean;
}

export const getVisibleCycleEvaluationGroupSteps = ({
    hasFeedback360,
}: CycleEvaluationGroupStepVisibility): CycleEvaluationGroupStepsType[] =>
    CYCLE_EVALUATION_GROUP_STEP_ORDER.filter(
        (step) => step !== CycleEvaluationGroupSteps.FEEDBACK_360_SETTINGS || hasFeedback360,
    );

export const findGroupEvaluationByType = (
    group: CycleEvaluationGroup | undefined,
    reviewType: EvaluationTypeValue,
): CycleEvaluationGroupChild | undefined =>
    (group?.cycleEvaluations ?? []).find((evaluation) => evaluation.reviewType === reviewType);

export const groupIncludesEvaluationType = (
    group: CycleEvaluationGroup | undefined,
    reviewType: EvaluationTypeValue,
): boolean => findGroupEvaluationByType(group, reviewType) !== undefined;

export const groupIncludesFeedback360 = (group?: CycleEvaluationGroup): boolean =>
    groupIncludesEvaluationType(group, EvaluationType.EVALUATION_360);

export type CycleEvaluationGroupPeriodOverride = Required<
    Pick<
        CycleEvaluationGroupUpdatePayload,
        'evaluationPeriodId' | 'evaluationPeriodStart' | 'evaluationPeriodEnd'
    >
>;

export interface BuildCycleEvaluationGroupUpdatePayloadParams {
    /** The cycle as it was last read: every field the caller does not override is re-sent from it. */
    group: CycleEvaluationGroup;
    /** Override the name (step 1 only). */
    name?: string;
    /** Override the period (step 1 only). All three fields or none. */
    period?: CycleEvaluationGroupPeriodOverride;
    status?: CycleEvaluationGroupStatusValue;
    currentStep?: CycleEvaluationGroupStepsType;
    communications?: CycleCommunicationBlockPayload[];
    audience?: CycleAudiencePayload;
    frequency?: FrequencyValue;
}

export const buildCycleEvaluationGroupUpdatePayload = ({
    group,
    name,
    period,
    status,
    currentStep,
    communications,
    audience,
    frequency,
}: BuildCycleEvaluationGroupUpdatePayloadParams): CycleEvaluationGroupUpdatePayload => {
    // All-or-nothing: merging period fields individually could silently restore cleared dates.
    const resolvedPeriod = period ?? {
        evaluationPeriodId: group.evaluationPeriodId ?? null,
        evaluationPeriodStart: group.evaluationPeriodStart ?? null,
        evaluationPeriodEnd: group.evaluationPeriodEnd ?? null,
    };

    // Keys spelled out explicitly — the key set must not depend on the caller's shape.
    return {
        name: name ?? group.name,
        evaluationPeriodId: resolvedPeriod.evaluationPeriodId,
        evaluationPeriodStart: resolvedPeriod.evaluationPeriodStart,
        evaluationPeriodEnd: resolvedPeriod.evaluationPeriodEnd,
        status: status ?? group.status,
        currentStep: currentStep ?? group.currentStep,
        // Conditional spread: absent key = "leave it alone" on the server.
        ...(communications !== undefined ? { communications } : {}),
        // Same conditional spread — absent key = leave audience alone.
        ...(audience !== undefined ? { audience } : {}),
        ...(frequency !== undefined ? { frequency } : {}),
    };
};

export interface ResolveCycleEvaluationGroupResumeStepParams
    extends CycleEvaluationGroupStepVisibility {
    persistedStep?: string;
    /** Steps the caller can mount. Omitted = all visible steps are safe. */
    renderableSteps?: readonly CycleEvaluationGroupStepsType[];
    /** If 360 feedback is required but not yet complete, don't resume beyond its settings step. */
    isFeedback360Complete?: boolean;
}

/** Resolves the step a draft reopens on — always visible and renderable. */
export const resolveCycleEvaluationGroupResumeStep = ({
    persistedStep,
    hasFeedback360,
    renderableSteps,
    isFeedback360Complete = true,
}: ResolveCycleEvaluationGroupResumeStepParams): CycleEvaluationGroupStepsType => {
    const visibleSteps = getVisibleCycleEvaluationGroupSteps({ hasFeedback360 });

    const resumableSteps = renderableSteps
        ? visibleSteps.filter((step) => renderableSteps.includes(step))
        : visibleSteps;

    const feedback360Rank = CYCLE_EVALUATION_GROUP_STEP_ORDER.indexOf(
        CycleEvaluationGroupSteps.FEEDBACK_360_SETTINGS,
    );

    const shouldClampToFeedback360 =
        hasFeedback360 &&
        !isFeedback360Complete &&
        isCycleEvaluationGroupStep(persistedStep) &&
        CYCLE_EVALUATION_GROUP_STEP_ORDER.indexOf(persistedStep) > feedback360Rank;

    const clampedStep = shouldClampToFeedback360
        ? CycleEvaluationGroupSteps.FEEDBACK_360_SETTINGS
        : persistedStep;

    if (!isCycleEvaluationGroupStep(clampedStep)) {
        return resumableSteps[0];
    }

    if (resumableSteps.includes(clampedStep)) {
        return clampedStep;
    }

    const clampedRank = CYCLE_EVALUATION_GROUP_STEP_ORDER.indexOf(clampedStep);

    // Fallback: prevents `undefined` if a second conditional step is added later.
    return (
        resumableSteps.find(
            (step) => CYCLE_EVALUATION_GROUP_STEP_ORDER.indexOf(step) > clampedRank,
        ) ?? resumableSteps[resumableSteps.length - 1]
    );
};

interface GetCycleEvaluationGroupFurthestStepIndexParams {
    group?: CycleEvaluationGroup;
    visibleSteps: CycleEvaluationGroupStepsType[];
    /** Forwarded as-is to `resolveCycleEvaluationGroupResumeStep` — see its own doc. */
    renderableSteps?: readonly CycleEvaluationGroupStepsType[];
    /** Forwarded as-is to `resolveCycleEvaluationGroupResumeStep` — see its own doc. */
    isFeedback360Complete?: boolean;
    /** Live 360 feedback state (from checkbox, not persisted). Defaults to persisted state. */
    hasFeedback360?: boolean;
}

export const getCycleEvaluationGroupFurthestStepIndex = ({
    group,
    visibleSteps,
    renderableSteps,
    isFeedback360Complete,
    hasFeedback360,
}: GetCycleEvaluationGroupFurthestStepIndexParams): number | undefined => {
    if (!group) {
        return undefined;
    }

    const resolvedHasFeedback360 = hasFeedback360 ?? groupIncludesFeedback360(group);

    const resumeStep = resolveCycleEvaluationGroupResumeStep({
        persistedStep: group.currentStep,
        hasFeedback360: resolvedHasFeedback360,
        renderableSteps,
        isFeedback360Complete,
    });

    const index = visibleSteps.indexOf(resumeStep);

    return index >= 0 ? index : undefined;
};

/**
 * The launch summary is the only screen that evaluates the launch guard, so it is also the only
 * way into the draft-to-active passage: every other entry point routes here rather than carrying
 * its own copy of the rule.
 */
export const getCycleEvaluationGroupLaunchSummaryPath = (groupId: string): string =>
    `${RouteName.CycleEvaluationGroupLaunchSummary}/${groupId}`;

export interface ResolveCycleEvaluationGroupLandingPathParams {
    group: CycleEvaluationGroup;
    renderableSteps?: readonly CycleEvaluationGroupStepsType[];
}

export const resolveCycleEvaluationGroupLandingPath = ({
    group,
    renderableSteps,
}: ResolveCycleEvaluationGroupLandingPathParams): string => {
    if (isCycleEvaluationGroupReadOnly(group)) {
        return getCycleEvaluationGroupLaunchSummaryPath(group.id);
    }

    const resumeStep = resolveCycleEvaluationGroupResumeStep({
        persistedStep: group.currentStep,
        hasFeedback360: groupIncludesFeedback360(group),
        renderableSteps,
    });

    return `${RouteName.CycleEvaluationGroupForm}/${getCycleEvaluationGroupStepPath(resumeStep)}/${group.id}`;
};
